import { prefersReducedMotion } from '../../game/haptics.ts';
import {
	type Cam,
	cellsBox,
	cellViewport,
	centreOn,
	clampCam,
	type CellViewport,
	FAR_SCALE,
	fitScale,
	followCam,
	type FollowTarget,
	levelScale,
	minimapCorner,
	minimapSize,
	pinchScale,
	rulers,
	snapLevel,
	type Stage,
	visibleCells,
	type ZoomLevel,
	zoomAbout,
} from './geometry.ts';

/** After a solve, let the sweep and Flow read before the camera moves on (ms). */
export const SOLVE_READ_MS = 380;
/** How long the minimap stays up after the camera stops (ms). */
const MINIMAP_HOLD_MS = 1400;
/** A pointer that moves further than this is a drag, not a tap (px). */
const TAP_SLOP = 7;
const DOUBLE_TAP_MS = 300;
const DOUBLE_TAP_PX = 30;

export interface CameraView {
	level: ZoomLevel;
	/** False when the whole board already fits at reading size (no zoom to offer). */
	canZoom: boolean;
}

export interface CameraParts {
	stage: HTMLElement;
	cam: HTMLElement;
	board: HTMLElement;
	minimap: HTMLElement;
	viewportRect: SVGRectElement;
	rulerH: HTMLElement;
	rulerV: HTMLElement;
}

/**
 * The big-board camera: one transform (translate + scale) on the element holding the
 * board, driven imperatively so a drag or pinch never re-renders React. React only
 * hears about the zoom level (for the header button and the arrows-only treatment).
 *
 * Lazy and direction-aware: it keeps the active answer and its clue in frame and moves
 * only when something needed leaves it (geometry.ts `followCam`).
 */
export class CameraController {
	private parts: CameraParts | null = null;
	private st: Stage | null = null;
	private cam: Cam = { x: 0, y: 0, s: 1 };
	private fit = 1;
	private view: CameraView = { level: 'read', canZoom: true };
	private listeners = new Set<() => void>();
	private target: FollowTarget | null = null;
	private lastSolveAt = -Infinity;
	private followTimer = 0;
	private mmTimer = 0;
	private animTimer = 0;
	private locked = false;
	/** A finished board: it rests whole, and only the zoom key (or a pinch from reading
	 *  size) moves it. No pan, tap-to-dive or pinch while it shows whole. */
	private resting = false;
	private startLevel: ZoomLevel = 'read';
	private placed = false;

	// --- React glue ---------------------------------------------------------------------
	subscribe = (fn: () => void) => {
		this.listeners.add(fn);
		return () => this.listeners.delete(fn);
	};
	getView = (): CameraView => this.view;
	private setView(next: CameraView) {
		if (next.level === this.view.level && next.canZoom === this.view.canZoom) return;
		this.view = next;
		for (const fn of this.listeners) fn();
	}

	/** Cells wholly in frame (where the camera is headed), for Flow navigation. */
	visibleCells = (): ReadonlySet<number> | undefined => (this.st ? visibleCells(this.cam, this.st) : undefined);

	/** The frame in cell units, for placing the Flow tag in view. */
	viewport = (): CellViewport | undefined => (this.st ? cellViewport(this.cam, this.st) : undefined);

	// --- Mounting -----------------------------------------------------------------------
	attach(parts: CameraParts, grid: { cols: number; rows: number }, initial: { level: ZoomLevel; cell: number | null }) {
		this.parts = parts;
		this.startLevel = initial.level;
		this.placed = false;
		const { stage } = parts;
		const measure = () => this.layout(grid, initial.cell);
		measure();
		const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
		ro?.observe(stage);
		ro?.observe(parts.board);
		// overflow: clip can't scroll, but older engines fall back to hidden, which focus
		// could scroll; the camera owns the position, so undo any such scroll.
		const unscroll = () => {
			if (stage.scrollLeft || stage.scrollTop) stage.scrollTo(0, 0);
		};
		stage.addEventListener('scroll', unscroll);
		const offGestures = this.bindGestures(stage);
		return () => {
			ro?.disconnect();
			stage.removeEventListener('scroll', unscroll);
			offGestures();
			window.clearTimeout(this.followTimer);
			window.clearTimeout(this.mmTimer);
			window.clearTimeout(this.animTimer);
			this.parts = null;
			this.st = null;
		};
	}

	private layout(grid: { cols: number; rows: number }, startCell: number | null) {
		const p = this.parts;
		if (!p) return;
		const W = p.stage.clientWidth;
		const H = p.stage.clientHeight;
		const bw = p.board.offsetWidth;
		const bh = p.board.offsetHeight;
		if (!W || !H || !bw) return;
		const fw = (bw - p.board.clientWidth) / 2;
		const cs = (bw - 2 * fw) / grid.cols;
		const prev = this.st;
		this.st = { W, H, bw, bh, cs, fw, cols: grid.cols, rows: grid.rows };
		this.fit = fitScale(this.st);
		p.stage.style.setProperty('--mm', `${minimapSize(W, grid.cols)}px`);
		const canZoom = this.fit < 0.99;
		if (!this.placed) {
			this.placed = true;
			const level = canZoom ? this.startLevel : 'read';
			if (level === 'whole') this.cam = clampCam({ x: 0, y: 0, s: this.fit }, this.st);
			else {
				const cell = this.target?.cursor ?? startCell ?? 0;
				// A down answer starts lower in the frame, so more of it shows.
				this.cam = centreOn(cell, this.st, this.target?.axis === 'down' ? 1.5 : 0);
				const next = this.target ? followCam(this.cam, this.st, this.target) : null;
				if (next) this.cam = next;
			}
			this.setView({ level, canZoom });
		} else {
			// Resized: keep the same level and the same board point at the centre.
			const level = canZoom ? this.view.level : 'read';
			const s = levelScale(level, this.fit);
			const cx = prev ? (prev.W / 2 - this.cam.x) / this.cam.s : 0;
			const cy = prev ? (prev.H / 2 - this.cam.y) / this.cam.s : 0;
			this.cam = clampCam({ s, x: W / 2 - cx * s, y: H / 2 - cy * s }, this.st);
			this.setView({ level, canZoom });
		}
		this.apply(false);
	}

	// --- Rendering ----------------------------------------------------------------------
	private apply(anim: boolean, cam: Cam = this.cam) {
		const p = this.parts;
		const st = this.st;
		if (!p || !st) return;
		const c = clampCam(cam, st);
		// At reading size the board lands on whole pixels, so its type stays crisp.
		this.cam = c.s === 1 ? { s: 1, x: Math.round(c.x), y: Math.round(c.y) } : c;
		const { x, y, s } = this.cam;
		const animate = anim && !prefersReducedMotion();
		if (animate) {
			p.stage.dataset.anim = '';
			window.clearTimeout(this.animTimer);
			this.animTimer = window.setTimeout(() => this.parts && delete this.parts.stage.dataset.anim, 340);
		} else {
			delete p.stage.dataset.anim;
		}
		p.cam.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${s})`;
		// Clue text gives way to arrows at the whole-board distance, mid-pinch included.
		if (s < FAR_SCALE) p.stage.dataset.far = '';
		else delete p.stage.dataset.far;
		this.paintRulers();
		this.paintMinimap();
	}

	private paintRulers() {
		const p = this.parts!;
		const st = this.st!;
		const r = rulers(this.cam, st);
		for (const [el, part, horizontal] of [
			[p.rulerH, r.h, true],
			[p.rulerV, r.v, false],
		] as const) {
			el.toggleAttribute('data-off', !part);
			if (!part) continue;
			const bar = el.firstElementChild as HTMLElement | null;
			if (!bar) continue;
			bar.style.setProperty(horizontal ? 'left' : 'top', `${part.start * 100}%`);
			bar.style.setProperty(horizontal ? 'width' : 'height', `${part.size * 100}%`);
		}
	}

	private paintMinimap() {
		const p = this.parts!;
		const st = this.st!;
		const v = cellViewport(this.cam, st);
		const x = Math.max(0, v.x);
		const y = Math.max(0, v.y);
		const r = p.viewportRect;
		r.setAttribute('x', String(x));
		r.setAttribute('y', String(y));
		r.setAttribute('width', String(Math.max(0, Math.min(st.cols, v.x + v.cols) - x)));
		r.setAttribute('height', String(Math.max(0, Math.min(st.rows, v.y + v.rows) - y)));
	}

	/** Show the minimap while the camera moves, in a corner clear of the answer. */
	private flashMinimap(hold = true) {
		const p = this.parts;
		const st = this.st;
		if (!p || !st) return;
		if (this.cam.s < FAR_SCALE) {
			delete p.minimap.dataset.show;
			return;
		}
		const size = minimapSize(st.W, st.cols);
		const t = this.target;
		const box = t ? cellsBox(t.clueCell === null ? t.cells : [...t.cells, t.clueCell], this.cam, st) : null;
		p.minimap.dataset.corner = minimapCorner(box, st.W, st.H, size);
		p.minimap.dataset.show = '';
		window.clearTimeout(this.mmTimer);
		if (hold) this.mmTimer = window.setTimeout(() => this.parts && delete this.parts.minimap.dataset.show, MINIMAP_HOLD_MS);
	}

	// --- Following ----------------------------------------------------------------------
	/** A solve just happened: the next camera move waits for its sweep to read. */
	noteSolve() {
		this.lastSolveAt = performance.now();
	}

	/** Where the camera should be looking, without moving it (before the first layout). */
	setTarget(target: FollowTarget | null) {
		this.target = target;
	}

	/** The active answer changed or the cursor moved: keep it in frame (lazily). */
	follow(target: FollowTarget | null) {
		this.target = target;
		window.clearTimeout(this.followTimer);
		if (!target || this.locked) return;
		const wait = prefersReducedMotion() ? 0 : Math.max(0, SOLVE_READ_MS - (performance.now() - this.lastSolveAt));
		const run = () => {
			if (!this.st || this.locked || this.view.level !== 'read') return;
			const next = followCam(this.cam, this.st, target);
			if (!next) return;
			this.apply(true, next);
			this.flashMinimap();
		};
		if (wait > 0) this.followTimer = window.setTimeout(run, wait);
		else run();
	}

	// --- Zoom ---------------------------------------------------------------------------
	private setLevel(level: ZoomLevel, cam: Cam, anim = true) {
		this.setView({ ...this.view, level });
		this.apply(anim, cam);
	}

	/** To the whole board, keeping (px, py) under the finger (default: the centre). */
	zoomOut(px?: number, py?: number) {
		const st = this.st;
		if (!st || !this.view.canZoom) return;
		this.setLevel('whole', zoomAbout(this.cam, this.fit, px ?? st.W / 2, py ?? st.H / 2, st));
		delete this.parts?.minimap.dataset.show;
	}

	/** Back to reading size, centred on a cell (or on the stage point px, py). */
	zoomIn(at: { cell: number } | { px: number; py: number }) {
		const st = this.st;
		if (!st) return;
		const cam = 'cell' in at ? centreOn(at.cell, st) : zoomAbout(this.cam, 1, at.px, at.py, st);
		this.setLevel('read', cam);
		if ('cell' in at && this.target) {
			const next = followCam(this.cam, st, this.target);
			if (next) this.apply(true, next);
		}
		this.flashMinimap();
	}

	/** The header button: whole board ⇄ reading size at the cursor (or the middle). */
	toggle = () => {
		if (this.locked) return;
		if (this.view.level === 'read') this.zoomOut();
		else if (this.target) this.zoomIn({ cell: this.target.cursor });
		else if (this.st) this.zoomIn({ px: this.st.W / 2, py: this.st.H / 2 });
	};

	/** The completion: the last zoom-out, then the camera rests. */
	finish() {
		window.clearTimeout(this.followTimer);
		this.locked = true;
		if (this.view.level !== 'whole') this.zoomOut();
		if (!this.st) this.startLevel = 'whole';
	}

	/** A reset: free the camera again. */
	unlock() {
		this.locked = false;
		this.resting = false;
	}

	/**
	 * The finished board: it shows whole (the completion already zoomed out; a solved
	 * puzzle reopens there) and holds still. The zoom key still offers reading size, and
	 * at reading size the board pans and pinches as usual.
	 */
	rest() {
		this.locked = false;
		this.resting = true;
	}

	/** Resting whole: gestures leave the board alone, so taps only read clues. */
	private get still() {
		return this.resting && this.view.level === 'whole';
	}

	get level() {
		return this.view.level;
	}

	// --- Gestures -----------------------------------------------------------------------
	private bindGestures(stage: HTMLElement) {
		const pts = new Map<number, { x: number; y: number }>();
		let start: { x: number; y: number; cam: Cam } | null = null;
		let moved = false;
		let pinch: { d: number; mx: number; my: number; cam: Cam } | null = null;
		let lastTap: { t: number; x: number; y: number; level: ZoomLevel } | null = null;
		let swallowClick = false;
		let zoomInOnClick: { px: number; py: number } | null = null;

		const local = (cx: number, cy: number) => {
			const r = stage.getBoundingClientRect();
			return [cx - r.left, cy - r.top] as const;
		};

		const down = (e: PointerEvent) => {
			if (this.locked || this.still || (e.pointerType === 'mouse' && e.button !== 0)) return;
			swallowClick = false;
			zoomInOnClick = null;
			pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
			if (pts.size === 1) {
				start = { x: e.clientX, y: e.clientY, cam: { ...this.cam } };
				moved = false;
			} else if (pts.size === 2) {
				const [a, b] = [...pts.values()] as [{ x: number; y: number }, { x: number; y: number }];
				for (const id of pts.keys()) capture(id);
				pinch = { d: Math.hypot(a.x - b.x, a.y - b.y) || 1, mx: (a.x + b.x) / 2, my: (a.y + b.y) / 2, cam: { ...this.cam } };
				moved = true;
				delete stage.dataset.anim;
				stage.dataset.moving = '';
			}
		};
		const capture = (id: number) => {
			try {
				stage.setPointerCapture(id);
			} catch {
				// The pointer is already gone.
			}
		};
		const move = (e: PointerEvent) => {
			if (!pts.has(e.pointerId) || !this.st) return;
			pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
			if (pinch && pts.size >= 2) {
				const [a, b] = [...pts.values()] as [{ x: number; y: number }, { x: number; y: number }];
				const s = pinchScale(pinch.cam.s, Math.hypot(a.x - b.x, a.y - b.y) / pinch.d, this.fit);
				const [mx0, my0] = local(pinch.mx, pinch.my);
				const [mx, my] = local((a.x + b.x) / 2, (a.y + b.y) / 2);
				const bx = (mx0 - pinch.cam.x) / pinch.cam.s;
				const by = (my0 - pinch.cam.y) / pinch.cam.s;
				this.apply(false, { s, x: mx - bx * s, y: my - by * s });
				return;
			}
			if (!start || pinch) return;
			const dx = e.clientX - start.x;
			const dy = e.clientY - start.y;
			if (!moved && Math.hypot(dx, dy) > TAP_SLOP) {
				moved = true;
				capture(e.pointerId);
				stage.dataset.moving = '';
			}
			if (moved) {
				this.apply(false, { ...start.cam, x: start.cam.x + dx, y: start.cam.y + dy });
				this.flashMinimap(false);
			}
		};
		const up = (e: PointerEvent) => {
			if (!pts.has(e.pointerId)) return;
			pts.delete(e.pointerId);
			if (pinch) {
				if (pts.size === 0) {
					pinch = null;
					start = null;
					swallowClick = true;
					delete stage.dataset.moving;
					const st = this.st;
					if (st) {
						const level = snapLevel(this.cam.s, this.fit);
						this.setLevel(level, zoomAbout(this.cam, levelScale(level, this.fit), st.W / 2, st.H / 2, st));
						if (level === 'read') this.flashMinimap();
					}
				}
				return;
			}
			if (!start) return;
			start = null;
			if (moved) {
				swallowClick = true;
				delete stage.dataset.moving;
				this.flashMinimap();
				return;
			}
			if (e.type !== 'pointerup') return;
			// A tap. The click that follows does the selecting; here we only decide zoom.
			const now = performance.now();
			const [px, py] = local(e.clientX, e.clientY);
			const double =
				lastTap && now - lastTap.t < DOUBLE_TAP_MS && Math.hypot(e.clientX - lastTap.x, e.clientY - lastTap.y) < DOUBLE_TAP_PX;
			if (double) {
				// The second tap zooms (relative to the level the first tap found), it doesn't select.
				swallowClick = true;
				if (lastTap!.level === 'read' && this.view.level === 'read') this.zoomOut(px, py);
				lastTap = null;
				return;
			}
			lastTap = { t: now, x: e.clientX, y: e.clientY, level: this.view.level };
			// In the overview, a tap dives in there; the click still selects what's under it.
			if (this.view.level === 'whole') zoomInOnClick = { px, py };
		};
		const click = (e: MouseEvent) => {
			if (swallowClick) {
				swallowClick = false;
				e.stopPropagation();
				e.preventDefault();
				return;
			}
			if (zoomInOnClick) {
				this.zoomIn(zoomInOnClick);
				zoomInOnClick = null;
			}
		};
		const wheel = (e: WheelEvent) => {
			if (this.locked || this.still || !this.st) return;
			e.preventDefault();
			if (e.ctrlKey) {
				// Trackpad pinch: snap on the first decisive event in each direction.
				const want: ZoomLevel = e.deltaY > 0 ? 'whole' : 'read';
				if (want === this.view.level) return;
				const [px, py] = local(e.clientX, e.clientY);
				if (want === 'whole') this.zoomOut(px, py);
				else this.zoomIn({ px, py });
				return;
			}
			const k = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? this.st.H : 1;
			this.apply(false, { ...this.cam, x: this.cam.x - e.deltaX * k, y: this.cam.y - e.deltaY * k });
			this.flashMinimap();
		};

		stage.addEventListener('pointerdown', down);
		stage.addEventListener('pointermove', move);
		stage.addEventListener('pointerup', up);
		stage.addEventListener('pointercancel', up);
		stage.addEventListener('click', click, true);
		stage.addEventListener('wheel', wheel, { passive: false });
		return () => {
			stage.removeEventListener('pointerdown', down);
			stage.removeEventListener('pointermove', move);
			stage.removeEventListener('pointerup', up);
			stage.removeEventListener('pointercancel', up);
			stage.removeEventListener('click', click, true);
			stage.removeEventListener('wheel', wheel);
		};
	}
}
